import { Fragment, useCallback, useEffect, useState } from "react";
import { ApiError } from "@/api/client";
import { adminApi } from "@/api/admin";
import { ConfirmDialog } from "@/components/ConfirmDialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import type { ReteResponsabile } from "@/types/admin";

// Il backend rifiuta con un codice (Task 8): qui diventa la frase approvata.
const MOTIVO_RIFIUTO: Record<string, string> = {
  utente_non_trovato: "Nessun utente CRM con questo nome utente.",
  ruolo_mancante: "L'utente non ha il profilo Responsabile Rete sul CRM.",
  gia_presente: "Questo utente è già un responsabile.",
};

function messaggioErrore(e: unknown): string {
  const codice = e instanceof ApiError ? e.detail : "";
  return MOTIVO_RIFIUTO[codice] ?? "Operazione non riuscita. Riprova tra poco.";
}

function euro(n: number): string {
  return `€${n.toLocaleString("it-IT", { minimumFractionDigits: 2 })}`;
}

export function ReteResponsabili() {
  const [rows, setRows] = useState<ReteResponsabile[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [nuovoAperto, setNuovoAperto] = useState(false);
  const [inModifica, setInModifica] = useState<number | null>(null);
  const [daDisattivare, setDaDisattivare] = useState<ReteResponsabile | null>(null);
  const [busy, setBusy] = useState(false);
  const [erroreAzione, setErroreAzione] = useState<string | null>(null);

  const carica = useCallback(async () => {
    try {
      setRows(await adminApi.reteResponsabili());
      setError(null);
    } catch {
      setError("Impossibile caricare i responsabili.");
    }
  }, []);
  useEffect(() => { void carica(); }, [carica]);

  async function cambiaAttivo(r: ReteResponsabile, attivo: boolean) {
    setBusy(true);
    setErroreAzione(null);
    try {
      await adminApi.reteAggiornaResponsabile(r.id, { attivo });
      setDaDisattivare(null);
      await carica();
    } catch (e) {
      setErroreAzione(messaggioErrore(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="rounded-lg border border-border bg-card overflow-hidden">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 px-4 py-3 border-b border-border">
        <h3 className="text-sm font-semibold text-foreground">Responsabili</h3>
        <Button size="sm" onClick={() => setNuovoAperto((v) => !v)}>Aggiungi responsabile</Button>
      </div>

      {nuovoAperto && (
        <FormNuovo
          onAnnulla={() => setNuovoAperto(false)}
          onSalvato={async () => { setNuovoAperto(false); await carica(); }}
        />
      )}

      {erroreAzione && daDisattivare === null && (
        <p role="alert" className="px-4 pt-3 text-sm text-destructive">{erroreAzione}</p>
      )}

      {error ? (
        <p className="px-4 py-6 text-sm text-destructive">{error}</p>
      ) : rows === null ? (
        <p className="px-4 py-6 text-sm text-muted-foreground">Caricamento...</p>
      ) : rows.length === 0 ? (
        <p className="px-4 py-6 text-sm text-muted-foreground">Nessun responsabile</p>
      ) : (
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Nome</TableHead>
                <TableHead className="hidden md:table-cell">Utente CRM</TableHead>
                <TableHead className="text-right">Agenzie in carico</TableHead>
                <TableHead className="text-right hidden md:table-cell">Importo a pratica</TableHead>
                <TableHead>Stato</TableHead>
                <TableHead className="text-right">Azioni</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((r) => (
                <Fragment key={r.id}>
                  <TableRow className={r.attivo ? "" : "opacity-60"}>
                    <TableCell className="font-medium text-foreground">{r.nome}</TableCell>
                    <TableCell className="hidden md:table-cell font-mono text-xs text-muted-foreground">
                      {r.username ?? r.espo_user_id}
                    </TableCell>
                    <TableCell className="text-right tabular-nums">{r.agenzie_in_carico}</TableCell>
                    <TableCell className="text-right tabular-nums hidden md:table-cell">{euro(r.importo_pratica)}</TableCell>
                    <TableCell>
                      <Badge
                        variant="outline"
                        className={r.attivo ? "border-emerald-400 text-emerald-700" : "border-border text-muted-foreground"}
                      >
                        {r.attivo ? "Attivo" : "Disattivato"}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-1.5 flex-wrap">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => setInModifica(inModifica === r.id ? null : r.id)}
                        >
                          Modifica
                        </Button>
                        {r.attivo ? (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => { setErroreAzione(null); setDaDisattivare(r); }}
                          >
                            Disattiva
                          </Button>
                        ) : (
                          <Button size="sm" variant="outline" disabled={busy} onClick={() => void cambiaAttivo(r, true)}>
                            Riattiva
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                  {inModifica === r.id && (
                    <TableRow>
                      <TableCell colSpan={6} className="bg-muted/20">
                        <FormModifica
                          responsabile={r}
                          onAnnulla={() => setInModifica(null)}
                          onSalvato={async () => { setInModifica(null); await carica(); }}
                        />
                      </TableCell>
                    </TableRow>
                  )}
                </Fragment>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      <ConfirmDialog
        open={daDisattivare !== null}
        title="Disattiva"
        message={
          daDisattivare
            ? `${daDisattivare.nome} non potrà più entrare. Le sue agenzie restano assegnate finché non le sposti. Confermi?`
            : ""
        }
        confirmLabel="Disattiva"
        busy={busy}
        error={erroreAzione}
        onConfirm={() => { if (daDisattivare) void cambiaAttivo(daDisattivare, false); }}
        onCancel={() => { setDaDisattivare(null); setErroreAzione(null); }}
      />
    </section>
  );
}

/**
 * Nuovo responsabile: parte da uno User CRM esistente col profilo «Responsabile Rete».
 * Il form compare solo su richiesta (progressive disclosure) e sta in testa alla tabella.
 */
function FormNuovo({ onAnnulla, onSalvato }: { onAnnulla: () => void; onSalvato: () => Promise<void> }) {
  const [username, setUsername] = useState("");
  const [nome, setNome] = useState("");
  const [importo, setImporto] = useState("0");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function salva(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    setErr(null);
    try {
      await adminApi.reteCreaResponsabile({
        username: username.trim(),
        nome: nome.trim() || null,
        importo_pratica: Number(importo) || 0,
      });
      await onSalvato();
    } catch (e2) {
      setErr(messaggioErrore(e2));
    } finally {
      setBusy(false);
    }
  }

  return (
    <form onSubmit={salva} className="border-b border-border bg-muted/20 p-4 space-y-3">
      <div className="grid gap-3 sm:grid-cols-3">
        <div className="flex flex-col gap-1">
          <label htmlFor="rete-nuovo-username" className="text-sm font-medium text-foreground">Nome utente CRM</label>
          <Input
            id="rete-nuovo-username"
            value={username}
            autoComplete="off"
            required
            aria-describedby="rete-nuovo-username-help"
            onChange={(e) => setUsername(e.target.value)}
          />
          <p id="rete-nuovo-username-help" className="text-xs text-muted-foreground">Lo stesso con cui entra nel portale</p>
        </div>
        <div className="flex flex-col gap-1">
          <label htmlFor="rete-nuovo-nome" className="text-sm font-medium text-foreground">Nome</label>
          <Input id="rete-nuovo-nome" value={nome} autoComplete="off" onChange={(e) => setNome(e.target.value)} />
        </div>
        <div className="flex flex-col gap-1">
          <label htmlFor="rete-nuovo-importo" className="text-sm font-medium text-foreground">Importo a pratica (€)</label>
          <Input
            id="rete-nuovo-importo"
            type="number"
            min={0}
            step="0.01"
            inputMode="decimal"
            className="tabular-nums"
            value={importo}
            onChange={(e) => setImporto(e.target.value)}
          />
        </div>
      </div>
      {err && <p role="alert" className="text-sm text-destructive">{err}</p>}
      <div className="flex justify-end gap-2">
        <Button size="sm" variant="outline" type="button" disabled={busy} onClick={onAnnulla}>Annulla</Button>
        <Button size="sm" type="submit" disabled={busy || username.trim().length === 0}>Salva</Button>
      </div>
    </form>
  );
}

/** Modifica in riga: nome e importo. Il backend registra un evento solo per i campi cambiati. */
function FormModifica({ responsabile, onAnnulla, onSalvato }: {
  responsabile: ReteResponsabile;
  onAnnulla: () => void;
  onSalvato: () => Promise<void>;
}) {
  const [nome, setNome] = useState(responsabile.nome);
  const [importo, setImporto] = useState(String(responsabile.importo_pratica));
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const idN = `rete-mod-${responsabile.id}-nome`;
  const idI = `rete-mod-${responsabile.id}-importo`;

  async function salva(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    setErr(null);
    try {
      await adminApi.reteAggiornaResponsabile(responsabile.id, {
        nome: nome.trim(),
        importo_pratica: Number(importo) || 0,
      });
      await onSalvato();
    } catch (e2) {
      setErr(messaggioErrore(e2));
    } finally {
      setBusy(false);
    }
  }

  return (
    // max-w: dentro la riga della tabella il form prenderebbe tutta la larghezza
    // e i campi si stirerebbero per centinaia di pixel.
    <form onSubmit={salva} className="space-y-3 max-w-2xl">
      <div className="grid gap-3 sm:grid-cols-2">
        <div className="flex flex-col gap-1">
          <label htmlFor={idN} className="text-sm font-medium text-foreground">Nome</label>
          <Input id={idN} value={nome} required onChange={(e) => setNome(e.target.value)} />
        </div>
        <div className="flex flex-col gap-1">
          <label htmlFor={idI} className="text-sm font-medium text-foreground">Importo a pratica (€)</label>
          <Input
            id={idI}
            type="number"
            min={0}
            step="0.01"
            inputMode="decimal"
            className="tabular-nums"
            value={importo}
            onChange={(e) => setImporto(e.target.value)}
          />
        </div>
      </div>
      {err && <p role="alert" className="text-sm text-destructive">{err}</p>}
      <div className="flex justify-end gap-2">
        <Button size="sm" variant="outline" type="button" disabled={busy} onClick={onAnnulla}>Annulla</Button>
        <Button size="sm" type="submit" disabled={busy || nome.trim().length === 0}>Salva</Button>
      </div>
    </form>
  );
}
